import React, { useState, useEffect, useRef } from "react";
import { Plus } from "lucide-react";
import Modal from "./Modal";
import PrimaryButton from "../components/Buttons/PrimaryButton";
import SecondaryButton from "../components/Buttons/SecondaryButton";

interface TagModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSubmit: (name: string, color: string) => Promise<void>;
  initialName?: string;
  initialColor?: string;
  mode?: "create" | "edit";
}

const PRESET_COLORS = [
  "#3B82F6",
  "#EF4444",
  "#10B981",
  "#F59E0B",
  "#8B5CF6",
  "#EC4899",
  "#06B6D4",
  "#F97316",
  "#14B8A6",
];

const TagModal: React.FC<TagModalProps> = ({
  isOpen,
  setIsOpen,
  onSubmit,
  initialName = "",
  initialColor = "#3B82F6",
  mode = "create",
}) => {
  const [name, setName] = useState(initialName);
  const [color, setColor] = useState(initialColor);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setColor(initialColor);
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen, initialName, initialColor]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError("Tag name is required");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onSubmit(name.trim(), color);
      setIsOpen(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to save tag");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      <div className="sm:flex sm:items-start">
        <div className="w-full text-left">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            {mode === "create" ? "Create new label" : "Edit Tag"}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="tag-name"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Label name
              </label>
              <input
                id="tag-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter label name"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Label colour
              </label>
              <div className="grid grid-cols-5 sm:grid-cols-10 gap-2">
                {PRESET_COLORS.map((presetColor) => (
                  <button
                    key={presetColor}
                    type="button"
                    onClick={() => setColor(presetColor)}
                    className={`aspect-square w-full rounded-md border-2 transition-all ${
                      color === presetColor
                        ? "border-gray-900 scale-100"
                        : "border-gray-200 hover:border-gray-400"
                    }`}
                    style={{ backgroundColor: presetColor }}
                    disabled={isSubmitting}
                    aria-label={`Select color ${presetColor}`}
                  />
                ))}
                <button
                  type="button"
                  onClick={() => colorInputRef.current?.click()}
                  className="aspect-square w-full rounded-md border-2 border-gray-200 hover:border-gray-400 transition-all flex items-center justify-center bg-white"
                  disabled={isSubmitting}
                  aria-label="Choose custom color"
                >
                  <Plus className="h-5 w-5 text-gray-600" />
                </button>
              </div>

              <input
                ref={colorInputRef}
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="absolute opacity-0 pointer-events-none"
                disabled={isSubmitting}
              />
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <SecondaryButton
                type="button"
                onClick={() => setIsOpen(false)}
                disabled={isSubmitting}
                className="flex-1"
              >
                Cancel
              </SecondaryButton>

              <PrimaryButton
                type="submit"
                disabled={isSubmitting || !name.trim()}
                className="flex-1"
              >
                {isSubmitting
                  ? "Saving..."
                  : mode === "create"
                    ? "Create label"
                    : "Save Changes"}
              </PrimaryButton>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};

export default TagModal;
